import React, { Component } from 'react';

/* ── Constants ───────────────────────────────────────────────── */
const GEO_RANGE_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];
const GEO_CHART_TYPES = [
  { key: 'bar', icon: '▌▌', label: 'Bar' },
  { key: 'hbar', icon: '══', label: 'Horizontal' },
  { key: 'table', icon: '☰', label: 'Table' },
];

/* ── Error boundary ──────────────────────────────────────────── */
class ChartErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ width: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 13 }}>
          Unable to load chart
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Empty state ─────────────────────────────────────────────── */
const EmptyState = ({ range }) => (
  <div style={{
    width: '100%', minHeight: '200px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: '8px', color: '#475569',
  }}>
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
    </svg>
    <span style={{ fontSize: '13px' }}>No geographic data for the last {range} days</span>
  </div>
);

/* ── Shared button style helper ──────────────────────────────── */
const ctrlBtn = (active) => ({
  padding: '4px 13px', borderRadius: 8, fontSize: 12, fontWeight: 700,
  border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.18s',
  background: active ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
  color: active ? '#a5b4fc' : '#475569',
  outline: active ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
});

/* ── Lazy-load Recharts ──────────────────────────────────────── */
let BarChartRC, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer;
try {
  const rc = require('recharts');
  BarChartRC = rc.BarChart; Bar = rc.Bar; XAxis = rc.XAxis; YAxis = rc.YAxis;
  CartesianGrid = rc.CartesianGrid; Tooltip = rc.Tooltip; ResponsiveContainer = rc.ResponsiveContainer;
} catch { /* recharts not available */ }

/* ── Vertical bar (Recharts) ─────────────────────────────────── */
const VerticalBar = ({ data }) => {
  if (!ResponsiveContainer) return null;
  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChartRC data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
          <XAxis dataKey="country" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip
            cursor={{ fill: 'rgba(99,102,241,0.1)' }}
            contentStyle={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: 12, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.5)' }}
            labelStyle={{ color: '#f1f5f9', fontWeight: 700, marginBottom: 4 }}
            itemStyle={{ color: '#818cf8', padding: '2px 0' }}
            formatter={(v) => [`${v} clicks`, 'Clicks']}
          />
          <Bar dataKey="clicks" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={32} />
        </BarChartRC>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Horizontal bar (custom SVG) ─────────────────────────────── */
const HorizontalBar = ({ data }) => {
  const [tooltip, setTooltip] = React.useState(null);
  const max = Math.max(...data.map(d => d.clicks), 1);
  const rowH = 28, padL = 80, padR = 60, padT = 8, barH = 14;
  const W = 680, H = padT * 2 + data.length * rowH;
  const chartW = W - padL - padR;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {tooltip && (
        <div style={{
          position: 'absolute', left: tooltip.x, top: tooltip.y - 44,
          transform: 'translateX(-50%)',
          background: '#1e2a45', border: '1px solid rgba(99,102,241,0.35)',
          borderRadius: 8, padding: '5px 11px', fontSize: 12, fontWeight: 700,
          color: '#e2e8f0', pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          <span style={{ color: '#818cf8' }}>{tooltip.label}</span><br />
          {tooltip.clicks} click{tooltip.clicks !== 1 ? 's' : ''}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="geoHBarGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        {data.map((d, i) => {
          const by = padT + i * rowH;
          const bw = Math.max(4, (d.clicks / max) * chartW);
          const cy = by + barH / 2 + 4;
          return (
            <g key={d.country}>
              {/* Country label */}
              <text x={padL - 8} y={cy} textAnchor="end" fill="#94a3b8" fontSize="11" fontFamily="Inter, sans-serif"
                dominantBaseline="middle">{d.country}</text>
              {/* Bar track */}
              <rect x={padL} y={by + 4} width={chartW} height={barH} rx={4} fill="rgba(99,102,241,0.08)" />
              {/* Bar fill */}
              <rect x={padL} y={by + 4} width={bw} height={barH} rx={4} fill="url(#geoHBarGrad)"
                style={{ cursor: 'crosshair' }}
                onMouseEnter={e => {
                  const r = e.currentTarget.closest('svg').getBoundingClientRect();
                  setTooltip({ x: (padL + bw) * (r.width / W), y: (by + 4) * (r.height / H), label: d.country, clicks: d.clicks });
                }}
                onMouseLeave={() => setTooltip(null)}
              />
              {/* Click count label */}
              <text x={padL + bw + 6} y={cy} fill="#475569" fontSize="10" fontFamily="Inter, sans-serif"
                dominantBaseline="middle">{d.clicks}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ── Table view ──────────────────────────────────────────────── */
const TableView = ({ data }) => {
  const total = data.reduce((s, d) => s + d.clicks, 0);
  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(99,102,241,0.15)' }}>
            {['#', 'Country', 'Clicks', 'Share'].map(h => (
              <th key={h} style={{
                padding: '8px 12px', textAlign: h === 'Clicks' || h === 'Share' || h === '#' ? 'right' : 'left',
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#475569'
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => {
            const pct = total > 0 ? ((d.clicks / total) * 100).toFixed(1) : '0.0';
            return (
              <tr key={d.country} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 12px', color: '#334155', textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{i + 1}</td>
                <td style={{ padding: '10px 12px', color: '#e2e8f0', fontWeight: 600 }}>{d.country}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44,
                    padding: '2px 10px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.18)',
                    borderRadius: 100, fontSize: 12, fontWeight: 700, color: '#a5b4fc'
                  }}>{d.clicks}</span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    <div style={{ width: 60, height: 5, background: 'rgba(99,102,241,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#6366f1,#818cf8)', borderRadius: 3 }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: 12, minWidth: 36, textAlign: 'right' }}>{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ── Main wrapper ────────────────────────────────────────────── */
const GeographicChart = ({ analytics, geoRange = 30, setGeoRange }) => {
  const [chartType, setChartType] = React.useState('bar');

  const hasData = analytics && analytics.geoData && analytics.geoData.length > 0;
  const chartData = hasData
    ? analytics.geoData.map(item => ({ country: item.country, clicks: parseInt(item.count) }))
    : [];

  return (
    <div>
      {/* ── Controls ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {/* Day range */}
        <div style={{ display: 'flex', gap: 4 }}>
          {GEO_RANGE_OPTIONS.map(r => (
            <button key={r.days} onClick={() => setGeoRange && setGeoRange(r.days)} style={ctrlBtn(geoRange === r.days)}>
              {r.label}
            </button>
          ))}
        </div>
        {/* Chart type */}
        <div style={{ display: 'flex', gap: 4 }}>
          {GEO_CHART_TYPES.map(ct => (
            <button key={ct.key} onClick={() => setChartType(ct.key)} title={ct.label} style={ctrlBtn(chartType === ct.key)}>
              {ct.icon} {ct.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Chart body ── */}
      {!hasData ? (
        <EmptyState range={geoRange} />
      ) : (
        <ChartErrorBoundary>
          {chartType === 'bar' && <VerticalBar data={chartData} />}
          {chartType === 'hbar' && <HorizontalBar data={chartData} />}
          {chartType === 'table' && <TableView data={chartData} />}
        </ChartErrorBoundary>
      )}
    </div>
  );
};

export default GeographicChart;
